import React, { memo, useState } from "react";
import {
  Controller,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from "react-hook-form";
import {
  Autocomplete,
  FormControl,
  InputAdornment,
  InputLabel,
  TextField,
} from "@mui/material";
import { MdSearch } from "@react-icons/all-files/md/MdSearch";
import Search from "./search";
import { RedAsterisk } from "../../../../../styles/controls";
import { IBankBranchDetails } from "../../../../../store/slices/ifscSlice";
import MessageViewer from "../../../../shared/messageViewer";

export interface IMobileAutocomplete {
  /**
   * Control label.
   */
  label?: string;
  /**
   * Control name.
   */
  name: string;
  /**
   * Rules for validation.
   */
  rules?:
    | Omit<
        RegisterOptions<FieldValues, string>,
        "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
      >
    | undefined;
  /**
   * Placeholder for the control.
   */
  placeholder?: string;
  /**
   * Title for the search dialog
   */
  title: string;
}

function SearchIFSC({
  label,
  name,
  placeholder,
  rules,
  title,
}: IMobileAutocomplete) {
  const [open, setOpen] = useState(false);
  const {
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const close = () => {
    setOpen(false);
  };

  const selection = (value: IBankBranchDetails | null | string) => {
    setValue(name, value, { shouldDirty: true });
    trigger(name);
    close();
  };
  const openSearch = () => {
    setOpen(true);
  };
  return (
    <div>
      <InputLabel>
        {label}
        <RedAsterisk>&thinsp;*</RedAsterisk>
      </InputLabel>
      <FormControl fullWidth>
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              fullWidth
              readOnly
              getOptionLabel={(option: IBankBranchDetails | string) =>
                typeof option === "object" ? option?.ifscCode : option
              }
              isOptionEqualToValue={(
                option: IBankBranchDetails | string,
                value: IBankBranchDetails | string
              ) =>
                typeof option === "object" && typeof value === "object"
                  ? option?.ifscCode === value?.ifscCode
                  : option === value
              }
              open={false}
              options={[]}
              onChange={(_, data) => {
                field.onChange(data);
              }}
              renderInput={({ ...params }) => (
                <TextField
                  {...params}
                  onClick={openSearch}
                  error={error !== undefined}
                  slotProps={{
                    input: {
                      name: name,
                      readOnly: true,
                      placeholder: placeholder,
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdSearch size={25} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          )}
        />
        {errors[name] && (
          <MessageViewer
            status="error"
            details={String(errors[name]?.message)}
          />
        )}
      </FormControl>
      {open && (
        <Search
          title={title}
          open={open}
          close={close}
          ifscSelection={selection}
        />
      )}
    </div>
  );
}

export default memo(SearchIFSC);
